import React from 'react';
import './POSProductDetails.scss';
import { POSPromotionTypes } from '../../../../helpers/POSCreator/POSPromotionTypes.enum';
import { CurrencyType } from '../../../../helpers/Product/ProductPriceHelper';
import { IPOSProductPreviewDetails } from '../POSCreator';
import {
  promotionHasPriceAtTheBottom,
  promotionIncludesPrevPrice,
  promotionIncludesPrice,
} from '../../../../helpers/POSCreator/POSCreatorHelper';
import challenge25Logo from '../../../../assets/images/challenge25.png';
import { getUEPMeasureText, UEPMeasure } from '../../../../helpers/POSCreator/UEPMeasure.enum';

export interface IProps {
  product: IPOSProductPreviewDetails;
  orientation: 'landscape' | 'portrait';
}

export const POSProductDetails = (props: IProps) => {
  const { product, orientation } = props;
  const {
    description,
    price,
    prevPrice,
    priceCurrency,
    prevPriceStrikethrough,
    prevPriceCurrency,
    weight,
    promotionType,
    drinkaware,
    challenge25,
    multibuyValue,
    multiBuyProductCount,
    multibuyValueCurrency,
    uepMeasure,
    uepPrice,
    uepPriceEach,
    uepMultibuyPrice,
    uepHalfPrice,
  } = product;

  const isPenceValue = (val?: string): boolean => {
    if (!val) return false;
    const n = parseFloat(val);
    return n > 0 && n < 1;
  };

  const toPenceDisplay = (val: string): string => Math.round(parseFloat(val) * 100).toString();

  const isDecimal = (value?: string) => {
    if (!value) {
      return false;
    }

    return value.indexOf('.') > 0;
  };

  const getMultibuyDigitClass = (): string => {
    if (!multibuyValue) return '';
    const n = parseFloat(multibuyValue);
    if (isPenceValue(multibuyValue)) return ' mb-digits-2';
    const display = n >= 10
      ? (isDecimal(multibuyValue) ? 'mb-digits-4' : 'mb-digits-2')
      : (isDecimal(multibuyValue) ? 'mb-digits-3' : 'mb-digits-1');
    return ` ${display}`;
  };

  const getBottomPriceDigitClass = (): string => {
    if (!price) return '';
    if (isPenceValue(price)) return ' bp-pence';
    const n = parseFloat(price);
    if (n >= 10) {
      return isDecimal(price) ? ' bp-digits-4' : ' bp-digits-2';
    }
    return isDecimal(price) ? ' bp-digits-3' : ' bp-digits-1';
  };

  const getPriceBasedOnPromotion = () => {
    switch (promotionType) {
      case POSPromotionTypes.Multibuy:
        return multibuyValue;
      default:
        return price;
    }
  };

  const getPromotionClassName = (price: string, _currency: CurrencyType) => {
    let className = '';

    if (isPenceValue(price)) {
      className += ' pence';
    }

    if (promotionType === POSPromotionTypes.Half_Price) {
      className += ' half-price';
    }

    if (promotionType === POSPromotionTypes.Better_Than_Half_Price) {
      className += ' better-than-half-price';
    }

    if (promotionType === POSPromotionTypes.Save_a_Third) {
      className += ' save-third';
    }

    if (promotionType === POSPromotionTypes.Multibuy) {
      className += ' multibuy';
    }

    if (promotionType === POSPromotionTypes.BOGOF) {
      className += ' bogof';
    }

    if (promotionType === POSPromotionTypes.No_Strikethrough) {
      className += ' no-strikethrough';
    }

    if (promotionHasPriceAtTheBottom(promotionType)) {
      className += ' price-bottom';
    }

    if (promotionType === POSPromotionTypes.Strikethrough) {
      className += ' strikethrough';
    }

    const priceAsFloat = parseFloat(price);

    if (priceAsFloat >= 10) {
      className += ' two-units';
    } else {
      className += ' one-unit';
    }

    if (isDecimal(price)) {
      className += ' decimal';
    }

    return className;
  };

  const constructPrice = (price: string, currency: CurrencyType, extraClass?: string) => {
    const isPence = isPenceValue(price);
    let displayedPrice = price;

    if (!displayedPrice) {
      displayedPrice = 'X';
    } else if (isPence) {
      displayedPrice = toPenceDisplay(price);
    }

    return (
      <div
        className={`pos-product-details-price-wrapper${extraClass ? `-${extraClass}` : ''}
        ${getPromotionClassName(price, currency)}`}
      >
        <div className={`pos-product-details-price-container ${getPromotionClassName(price, currency)}`}>
          <div className={`pos-product-details-currency ${getPromotionClassName(price, currency)}`}>
            {isPence ? 'p' : '£'}
          </div>
          <div className={`pos-product-details-price ${getPromotionClassName(price, currency)}`}>
            {displayedPrice}
          </div>
          {
            (prevPriceStrikethrough || (extraClass === 'prev' && promotionIncludesPrevPrice(promotionType))) &&
            <div className={`pos-product-details-price-strikethrough ${getPromotionClassName(price, currency)}`} />
          }
        </div>
        {extraClass && promotionType === POSPromotionTypes.Multibuy ? 'each' : ''}
      </div>
    );
  };

  const getUEPClassName = (uepValue: string | undefined) => {
    if (!uepValue) {
      return '';
    }

    let className = '';
    const priceAsFloat = parseFloat(uepValue);
    const fullText = `£${priceAsFloat.toFixed(2)} ${getUEPMeasureText(uepMeasure || UEPMeasure.Each)}`;

    if (priceAsFloat >= 10) {
      className += ' two-digits';
    } else {
      className += ' one-digit';
    }

    if (isDecimal(uepValue)) {
      className += ' decimal';
    }

    if (fullText.length > 12) {
      className += ' long-text';
    }

    return className;
  };

  const formatUEPPrice = (value?: string): string => {
    if (!value) return '£X';
    const n = parseFloat(value);
    if (isNaN(n) || n === 0) return '£X';
    if (n > 0 && n < 1) {
      return `${Math.round(n * 100)}p`;
    }
    return `£${value}`;
  };

  const getInlineUEP = (uepValue: string | undefined) => {
    if (uepMeasure === undefined || uepMeasure === null) {
      return null;
    }

    const priceDisplay = formatUEPPrice(uepValue);
    const measureText = getUEPMeasureText(uepMeasure);

    return (
      <span className={`pos-product-details-uep-inline${getUEPClassName(uepValue)}`}>
        {priceDisplay} {measureText}
      </span>
    );
  };

  const getTitleBasedOnPromotion = () => {
    switch (promotionType) {
      case POSPromotionTypes.POS_General:
        return '';
      case POSPromotionTypes.Half_Price:
      case POSPromotionTypes.Better_Than_Half_Price:
        return 'Half Price';
      case POSPromotionTypes.Save_a_Third:
        return orientation === 'portrait' ? <><span>Save</span><br /><span>1/3</span></> : 'Save 1/3';
      case POSPromotionTypes.BOGOF:
        if (orientation === 'portrait') return 'FREE';
        return <><span>Buy one</span><br /><span>Get one FREE</span></>;
      case POSPromotionTypes.Multibuy:
        if (orientation === 'portrait') {
          return (
            <>
              <span className="multibuy-count">{multiBuyProductCount}</span>
              <span className="multibuy-for">for</span>
              {constructPrice(multibuyValue, multibuyValueCurrency)}
              {getInlineUEP(uepMultibuyPrice)}
            </>
          );
        }
        return (
          <>
            <p>Any</p>
            {multiBuyProductCount}
            <p> for </p >
            {constructPrice(multibuyValue, multibuyValueCurrency)}
          </>
        );
      default:
        return constructPrice(price, priceCurrency);
    }
  };

  const getHeaderBasedOnPromotion = () => {
    let value = null;
    switch (promotionType) {
      case POSPromotionTypes.Better_Than_Half_Price:
        value = 'Better Than';
        break;
      case POSPromotionTypes.BOGOF:
        if (orientation === 'portrait') {
          value = <><span>Buy one</span><br /><span>Get one</span></>;
        }
        // Landscape: no header, title contains full "Buy one / Get one FREE" text
        break;
      case POSPromotionTypes.Multibuy:
        // Portrait: header content merged into title for single-element centering
        // Landscape: no header (title already contains "Any {count} for {price}")
        break;
      default:
        value = '';
    }
    if (value) {
      return (
        <div className={`pos-product-details-header ${getPromotionClassName(price, priceCurrency)}`}>
          {value}
        </div>
      );
    }
  };

  const getDynamicDescFontSize = (
    textLength: number,
    maxSize: number,
    containerWidthPt: number,
    maxLines: number,
    minSize: number,
    charWidthRatio = 0.42,
    lineHeight = 0,
  ): number => {
    const maxTotalHeight = lineHeight > 0 ? maxSize * lineHeight * 1.6 : 0;

    let fontSize = maxSize;
    while (fontSize > minSize) {
      const charsPerLine = Math.floor(containerWidthPt / (charWidthRatio * fontSize));
      const lines = Math.ceil(textLength / charsPerLine);
      const heightLines = lines <= 1 ? 1 : maxLines;
      const fitsHeight = maxTotalHeight <= 0 || heightLines * fontSize * lineHeight <= maxTotalHeight;
      if (lines <= maxLines && fitsHeight) break;
      fontSize--;
    }

    return fontSize;
  };

  const getDescriptionBasedOnPromotion = () => {
    let value = '';
    const isBottomPriceLandscape = promotionHasPriceAtTheBottom(promotionType) && orientation === 'landscape';
    let className = `pos-product-details-desc ${isBottomPriceLandscape ? 'small' : ''}
      ${getPromotionClassName(price, priceCurrency)}`;
    let dynamicStyle: React.CSSProperties = {};

    switch (promotionType) {
      case POSPromotionTypes.POS_General:
        value = description;
        className += ' pre pos-general';
        break;
      default:
        value = orientation === 'portrait' ? description : `${description} ${weight}`;
    }

    if (promotionType !== POSPromotionTypes.POS_General) {
      const descText = value.trim() || 'Description';

      if (orientation === 'portrait') {
        const isBottomPrice = promotionHasPriceAtTheBottom(promotionType);
        const maxSize = isBottomPrice ? 70 : 86;
        const fontSize = getDynamicDescFontSize(descText.length, maxSize, 523, 3, 35, 0.45, 1.168);
        dynamicStyle = { fontSize: `${fontSize}pt` };
      } else {
        const fontSize = getDynamicDescFontSize(descText.length, 21, 380, 2, 12, 0.5);
        dynamicStyle = { fontSize: `${fontSize}pt` };
      }
    }

    return (
      <div className={className} style={dynamicStyle}>
        {value.trim() ? value : 'Description'}
      </div>
    );
  };

  const getDimensionsBasedOnPromotion = () => {
    if (promotionType === POSPromotionTypes.POS_General) return null;
    switch (promotionType) {
      default:
        return (
          <div
            className={`pos-product-details-dimensions ${
              (promotionHasPriceAtTheBottom(promotionType) && orientation === 'landscape') ? 'small' : ''
            }`}
          >
            {weight ? weight : ''}
          </div>
        );
    }
  };

  const getSubtitleBasedOnPromotion = () => {
    let value = null;

    if (promotionIncludesPrice(promotionType) && !promotionHasPriceAtTheBottom(promotionType)) {
      value = 'each';
    }

    switch (promotionType) {
      case POSPromotionTypes.Multibuy:
        value = (
          <span className="subtitle-price-group">
            {constructPrice(price, priceCurrency, 'multibuy')}
            {getInlineUEP(uepPriceEach)}
          </span>
        );
        break;
      default:
        break;
    }

    if (value) {
      return (
        <div className="pos-product-details-subtitle">
          {value}
        </div>
      );
    }
  };

  const getPrevPriceBasedOnPromotion = () => {
    if (promotionType === POSPromotionTypes.Multibuy) {
      if (orientation === 'landscape') {
        return (
          <div className="prev-price-with-uep">
            {constructPrice(price, priceCurrency, 'prev')}
            {getInlineUEP(uepPriceEach)}
          </div>
        );
      }
      return null;
    }

    if (!promotionIncludesPrevPrice(promotionType)) {
      return null;
    }

    let prevPriceComponent = <></>;

    if (parseFloat(prevPrice) > 0) {
      prevPriceComponent = constructPrice(prevPrice, prevPriceCurrency, 'prev');
    }

    return prevPriceComponent;
  };

  const getBottomPriceUEP = () => {
    let uepValue: string | undefined;

    switch (promotionType) {
      case POSPromotionTypes.Half_Price:
        uepValue = uepHalfPrice;
        break;
      case POSPromotionTypes.Better_Than_Half_Price:
      case POSPromotionTypes.Save_a_Third:
        uepValue = uepPrice;
        break;
      case POSPromotionTypes.BOGOF:
        uepValue = uepPriceEach;
        break;
      default:
        return null;
    }

    return getInlineUEP(uepValue);
  };

  const getUEPDisplay = () => {
    if (promotionType === POSPromotionTypes.POS_General) {
      return null;
    }

    // Landscape uses inline UEP next to the price, not corner UEP
    if (orientation === 'landscape') {
      return null;
    }

    // These promotion types use inline UEP, not corner UEP
    if (
      promotionType === POSPromotionTypes.Half_Price ||
      promotionType === POSPromotionTypes.Better_Than_Half_Price ||
      promotionType === POSPromotionTypes.Save_a_Third ||
      promotionType === POSPromotionTypes.Multibuy ||
      promotionType === POSPromotionTypes.BOGOF
    ) {
      return null;
    }

    let displayUEPPrice = '';

    switch (promotionType) {
      case POSPromotionTypes.No_Strikethrough:
      case POSPromotionTypes.Strikethrough:
        displayUEPPrice = uepPrice;
        break;
      default:
        break;
    }

    if (uepMeasure === undefined || uepMeasure === null) {
      return null;
    }

    const priceDisplay = formatUEPPrice(displayUEPPrice);
    const measureText = getUEPMeasureText(uepMeasure);

    return (
      <div className={`pos-product-details-uep ${orientation}${getUEPClassName(displayUEPPrice)}`}>
        {priceDisplay} {measureText}
      </div>
    );
  };

  return (
    <div className={`pos-product-details-container ${orientation} ${getPromotionClassName(price, priceCurrency)}`}>
      {orientation === 'portrait' && promotionType === POSPromotionTypes.Strikethrough && getPrevPriceBasedOnPromotion()}
      {getHeaderBasedOnPromotion()}
      <div
        className={`pos-product-details-title-wrapper ${
          getPromotionClassName(price, priceCurrency)
        }${promotionType === POSPromotionTypes.Multibuy
          ? getMultibuyDigitClass() : ''}`}
      >
        {orientation === 'landscape' && promotionType === POSPromotionTypes.Strikethrough && getPrevPriceBasedOnPromotion()}
        <div className={`pos-product-details-title-container ${isPenceValue(price) ? 'pence' : ''}`}>
          <div className={`pos-product-details-title ${getPromotionClassName(getPriceBasedOnPromotion(), priceCurrency)}`}>
            {getTitleBasedOnPromotion()}
          </div>
        </div>
        {orientation === 'landscape' &&
         (promotionType === POSPromotionTypes.No_Strikethrough || promotionType === POSPromotionTypes.Strikethrough) &&
         getInlineUEP(uepPrice)}
      </div>
      {orientation === 'landscape' && promotionType === POSPromotionTypes.Multibuy &&
        <div className={`multibuy-uep-row${getMultibuyDigitClass()}`}>
          {getInlineUEP(uepMultibuyPrice)}
        </div>
      }
      {
        promotionIncludesPrice(promotionType) && getSubtitleBasedOnPromotion()
      }
      {
        (!promotionHasPriceAtTheBottom(promotionType)) &&
        !(orientation === 'portrait' && promotionType === POSPromotionTypes.Strikethrough) &&
        !(orientation === 'landscape' && promotionType === POSPromotionTypes.Strikethrough) &&
        getPrevPriceBasedOnPromotion()
      }
      {
        (promotionHasPriceAtTheBottom(promotionType) && orientation === 'landscape') &&
        <div className={`bottom-price${getBottomPriceDigitClass()}`}>
          <div className="bottom-price-prices">
            {getPrevPriceBasedOnPromotion()}
            {constructPrice(price, priceCurrency)}
          </div>
          {getBottomPriceUEP()}
        </div>
      }
      {
        promotionHasPriceAtTheBottom(promotionType) && orientation !== 'landscape' &&
        <div className="bottom-price">
          {getPrevPriceBasedOnPromotion()}
          <div className="bottom-price-current">
            {constructPrice(price, priceCurrency)}
            {getBottomPriceUEP()}
          </div>
        </div>
      }
      {(promotionType === POSPromotionTypes.Multibuy ||
        promotionHasPriceAtTheBottom(promotionType)) &&
        orientation === 'portrait' ? (
        <div className={`desc-dims-group ${promotionType === POSPromotionTypes.Multibuy ? 'multibuy' : 'bottom-price'}`}>
          {getDescriptionBasedOnPromotion()}
          {getDimensionsBasedOnPromotion()}
        </div>
      ) : (
        <>
          {getDescriptionBasedOnPromotion()}
          {getDimensionsBasedOnPromotion()}
        </>
      )}
      <div className="pos-product-details-stickers">
        {
          drinkaware && <p>be <b>drinkaware</b>.co.uk</p>
        }
        {
          challenge25 && <img alt="drinkaware/challenge25 logo" src={challenge25Logo} />}
      </div>
      {getUEPDisplay()}
    </div>
  );
};
